"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { Star, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface RouteRatingProps {
  blockId: Id<"blocks">;
  rating?: number;
  readOnly?: boolean;
  size?: "sm" | "md" | "lg";
}

export function RouteRating({
  blockId,
  rating,
  readOnly = false,
  size = "md",
}: RouteRatingProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const updateRating = useMutation(api.blocks.updateRouteRating);

  const displayRating = hoverRating ?? rating ?? 0;

  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  const handleClick = async (value: number) => {
    if (readOnly || isUpdating) return;

    setIsUpdating(true);
    try {
      await updateRating({ blockId, rating: value });
    } catch (error) {
      console.error("Failed to update rating:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isUpdating) {
    return (
      <div className="flex items-center gap-1">
        <Loader2 className={cn(sizeClasses[size], "animate-spin text-muted-foreground")} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-0.5",
        !readOnly && "cursor-pointer"
      )}
      onMouseLeave={() => !readOnly && setHoverRating(null)}
    >
      {[1, 2, 3, 4, 5].map((value) => (
        <button
          key={value}
          type="button"
          disabled={readOnly}
          onClick={() => handleClick(value)}
          onMouseEnter={() => !readOnly && setHoverRating(value)}
          className={cn(
            "focus:outline-none transition-colors",
            readOnly && "cursor-default"
          )}
        >
          <Star
            className={cn(
              sizeClasses[size],
              "transition-colors",
              value <= displayRating
                ? "fill-yellow-400 text-yellow-400"
                : "text-muted-foreground"
            )}
          />
        </button>
      ))}
    </div>
  );
}

interface RatingDisplayProps {
  rating?: number;
  size?: "sm" | "md";
}

export function RatingDisplay({ rating, size = "sm" }: RatingDisplayProps) {
  if (!rating) return null;

  const sizeClasses = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
  };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((value) => (
        <Star
          key={value}
          className={cn(
            sizeClasses[size],
            value <= rating
              ? "fill-yellow-400 text-yellow-400"
              : "text-muted-foreground/30"
          )}
        />
      ))}
    </div>
  );
}
